import {forwardRef} from 'react'
import {Flex, Text, Box, Center, Icon, Image} from '@chakra-ui/react'
import GlobalButton from '@components/GlobalButton/GlobalButton'
import {ReactComponent as Check} from '@assets/svgs/roadmap/check.svg'
import {ReactComponent as Lock} from '@assets/svgs/roadmap/lock.svg'
import AvatarTask from '@assets/images/roadmap/avatar-task.png'
import {isLoggedIn, trackingEvent, trackingPage} from '@utils'
import {useNavigate} from 'react-router-dom'
import {EVENT_NAME, PATH, SCREEN_TRACKING} from '@utils/Const'
import mapRequest from '@apis/map'
import {
  updateEntryPointShowUpgradeModal,
  updateIsShowUpgradeModal,
} from '@redux/map/mapSlice'
import {useDispatch, useSelector} from 'react-redux'

interface TaskProps {
  task_id: string
  is_summary: boolean
  is_lock: boolean
  image: string
  index: number
  type:
    | 'LESSON_PART_ONE'
    | 'LESSON_PART_TWO'
    | 'REAL_TEST'
    | 'MOCK_TEST_PART_ONE'
    | 'MOCK_TEST_PART_TWO'
    | 'REAL_TEST_PART_ONE'
    | 'REAL_TEST_PART_TWO'
    | 'VOCABULARY'
    | 'IPA'
    | 'PHRASE_PRACTICE'
    | 'SENTENCE_PRACTICE'
    | 'LESSON_PART_TWO_NEW'
    | 'LESSON_PART_THREE'
    | 'LESSON'
  title: string
  duration: string
  status: 'completed' | 'current' | 'locked' | undefined
  input_params: any
  week_name: any
  is_finish_week: any
  is_course_available: boolean
}

const Task = forwardRef<HTMLDivElement, TaskProps>((props, ref) => {
  const {isCompleteFirstTask} = useSelector((state: any) => state.map)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const handleNavigation = () => {
    switch (props.type) {
      case 'LESSON_PART_ONE':
      case 'LESSON_PART_TWO':
        return navigate(PATH.CONVERSATION, {
          state: {
            entry_point: 'ROADMAP',
            task_id: props.task_id,
            task_type: props.type,
            lesson_id: props.input_params.lesson_id,
            lesson_name: props.input_params.lesson_name,
            feature_name: props.type,
          },
        })
      case 'VOCABULARY':
        return navigate(PATH.LEARN_VOCABULARY_OVERVIEW, {
          state: {
            entry_point: 'ROADMAP',
            task_id: props.task_id,
            setId: props.input_params.set_id,
          },
        })
      case 'REAL_TEST':
      case 'MOCK_TEST_PART_ONE':
      case 'REAL_TEST_PART_ONE':
      case 'MOCK_TEST_PART_TWO':
      case 'REAL_TEST_PART_TWO':
        return navigate(PATH.EXAM_ROOM, {
          state: {
            entry_point: 'ROADMAP',
            task_id: props.task_id,
            testId: props.input_params.test_id,
            type: props.input_params.type,
            feature_name: props.type,
          },
        })
      case 'IPA':
        return navigate(PATH.GYM_PRONOUNCE, {
          state: {
            entry_point: 'ROADMAP',
            task_id: props.task_id,
            lesson_id: props.input_params.lesson_id,
            lesson_name: props.input_params.lesson_name,
            type: props.input_params.ipa_lesson_detail_type,
          },
        })
      case 'LESSON_PART_TWO_NEW':
        return navigate(PATH.LEARN_PART_TWO, {
          state: {
            entry_point: 'ROADMAP',
            task_id: props.task_id,
            lesson_id: props.input_params.lesson_id,
            task_type: props.type,
          },
        })
      case 'LESSON_PART_THREE':
      case 'LESSON':
        return navigate(PATH.LEARN_PART_THREE, {
          state: {
            entry_point: 'ROADMAP',
            task_id: props.task_id,
            lesson_id: props.input_params.lesson_id,
            task_type: props.type,
          },
        })
      default:
        break
    }
  }

  const handleAuthCheck = () => {
    if (!isLoggedIn()) {
      navigate(PATH.LOGIN)
      return false
    }
    return true
  }

  const handlePremiumCheck = () => {
    if (props.is_lock) {
      if (!handleAuthCheck()) return false

      trackingPage(SCREEN_TRACKING.SALE_SCREEN_VIEW, {
        entry_point: 'roadmap_page',
      })
      dispatch(updateIsShowUpgradeModal(true))
      dispatch(updateEntryPointShowUpgradeModal('roadmap_page'))
      return false
    }
    return true
  }

  const trackTaskEvent = () => {
    if (props.is_summary) {
      trackingEvent(
        props.is_finish_week
          ? EVENT_NAME.MAP_WEEK_SUMMARY_CONTINUE_LEARN
          : EVENT_NAME.MAP_LESSON_SUMMARY_CONTINUE_LEARN,
      )
    } else {
      trackingEvent(EVENT_NAME.MAP_LESSON_START, {
        week_name: props.week_name,
        lesson_name: props.title || '',
      })
    }
  }

  const handleClickTask = () => {
    if (!isLoggedIn() && isCompleteFirstTask) {
      navigate(PATH.LOGIN)
      return
    }

    if (!props.is_course_available) {
      alert('Bạn chưa được mở lộ trình hiện tại')
      return
    }

    if (!handlePremiumCheck()) return
    if (props.status === 'locked') return

    if (isLoggedIn()) {
      updateCurrentTask()
    }

    trackTaskEvent()
    handleNavigation()
  }

  const updateCurrentTask = () => {
    try {
      mapRequest.updateCurrentTask({
        task_id: props.task_id,
      })
    } catch (error) {}
  }

  return (
    <Flex
      ref={ref}
      alignItems="center"
      p={{base: '16px', md: '24px'}}
      borderRadius="24px"
      bg={'white'}
      cursor={'pointer'}
      borderColor={props.status === 'current' ? '#99CEFF' : 'transparent'}
      borderWidth={'2px'}
      borderBottomWidth={'4px'}
      _hover={{
        borderColor: '#99CEFF',
        borderWidth: '2px',
        borderBottomWidth: '4px',
      }}
      onClick={handleClickTask}>
      <Center
        flexShrink={0}
        h={{base: '32px', md: '48px'}}
        w={{base: '32px', md: '48px'}}
        bg="white"
        borderRadius="full"
        boxShadow="sm"
        overflow={'hidden'}>
        <Image
          src={props.image ? props.image : AvatarTask}
          w={'100%'}
          h={'100%'}
        />
      </Center>
      <Box ml={4} flexGrow={1}>
        <Text
          fontSize={{base: '14px', md: '16px'}}
          fontWeight="600"
          color="#101828">
          {`${props.index + 1 < 10 ? '0' : ''}${props.index + 1}. ${props.title}`}
        </Text>
        <Text fontSize="12px" fontWeight="500" color="#667085">
          {props.duration}
        </Text>
      </Box>
      <Box ml={4}>
        {props.status === 'completed' && (
          <Center
            bg={'#DCFAE6'}
            w={'24px'}
            h={'24px'}
            borderRadius={'24px'}
            border={'1px solid #DCFAE6'}>
            <Icon as={Check} />
          </Center>
        )}
        {props.status === 'current' && (
          <GlobalButton borderRadius={'12px'} fontSize={'14px'}>
            {props.is_summary ? 'Bài tiếp theo' : 'Bắt đầu'}
          </GlobalButton>
        )}
        {props.status === 'locked' && <Icon as={Lock} w={'24px'} h={'24px'} />}
      </Box>
    </Flex>
  )
})

export default Task
